import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Edit, Trash2, Calendar } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

type CampaignReport = {
  id: string;
  brandId: string;
  campaignName: string;
  reportType: 'new-brand' | 'existing-brand' | 'mid-campaign';
  campaignStartDate: string;
  campaignEndDate?: string;
  preCampaignStartDate?: string;
  preCampaignEndDate?: string;
  reportDate?: string;
  status: 'draft' | 'published' | 'archived';
  createdAt: string;
  updatedAt: string;
  Brand?: {
    id: string;
    name: string;
  };
  creator?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
};

type Brand = {
  id: string;
  name: string;
};

export default function AdminReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<CampaignReport[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [filterBrandId, setFilterBrandId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  async function loadBrands() {
    try {
      const res = await fetch('/api/admin/brands', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  }

  async function loadReports() {
    try {
      const params = new URLSearchParams();
      if (filterBrandId) params.append('brandId', filterBrandId);
      if (filterStatus) params.append('status', filterStatus);

      const res = await fetch(`/api/admin/reports?${params.toString()}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
    }
  }

  useEffect(() => {
    loadBrands();
    loadReports();
    checkSuperAdmin();
  }, [filterBrandId, filterStatus]);

  async function checkSuperAdmin() {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const roles: string[] = data.roles || [];
        setIsSuperAdmin(roles.includes('superadmin'));
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  }

  async function handleDelete(reportId: string) {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/reports/${reportId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        loadReports();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to delete report');
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report');
    }
  }

  const filteredReports = reports.filter((report) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        report.campaignName.toLowerCase().includes(searchLower) ||
        report.Brand?.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'published':
        return 'default';
      case 'draft':
        return 'secondary';
      case 'archived':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Campaign Reports</h1>
        <Button onClick={() => navigate('/admin/reports/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Report
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Select value={filterBrandId || 'all'} onValueChange={(value) => setFilterBrandId(value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={filterStatus || 'all'} onValueChange={(value) => setFilterStatus(value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Campaign Dates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No reports found. Create your first report to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">{report.campaignName}</TableCell>
                    <TableCell>{report.Brand?.name || 'Unknown'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {report.reportType === 'new-brand' ? 'New Brand' : report.reportType === 'existing-brand' ? 'Existing Brand' : 'Mid-Campaign'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(report.campaignStartDate).toLocaleDateString()}
                        {report.campaignEndDate ? ` - ${new Date(report.campaignEndDate).toLocaleDateString()}` : ' (Ongoing)'}
                        {report.reportDate && (
                          <span className="ml-2 text-xs">(Report: {new Date(report.reportDate).toLocaleDateString()})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(report.status)}>
                        {report.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(report.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/reports/${report.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {(report.status === 'draft' || (report.status === 'published' && isSuperAdmin)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/reports/${report.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(report.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

